/**
 * LIVORA NATIVE CALL MIGRATION — PHASE N2A.
 *
 * One lightweight startup trace per callId, spanning both the caller and
 * receiver side of this JS runtime. The callId itself is the correlation
 * key across processes (this tab's JS, and the Android native side, which
 * emits the same [callStartup] event names from its own clock) - there is
 * no shared wall clock between JS and native, so "elapsedMs" is always
 * relative to this trace's own first event, never an absolute cross-process
 * timestamp.
 *
 * No tokens, balances, names, or other private data are ever logged here -
 * only callId, event name, elapsed milliseconds, and role.
 */

const traceStartByCallId = new Map();

function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/**
 * Idempotent - the first call for a callId wins and defines elapsedMs=0.
 * Safe to call again for the same callId (no-op).
 */
export function startCallStartupTrace(callId) {
  const key = String(callId || "");
  if (!key || traceStartByCallId.has(key)) {
    return;
  }
  traceStartByCallId.set(key, now());
}

/**
 * Same as startCallStartupTrace, but backdates the origin to an already
 * captured performance.now() value - used when the real origin (e.g. a
 * button tap) happened before callId existed, so events logged just after
 * callId arrives can still report accurate elapsed time back to the tap.
 */
export function startCallStartupTraceAt(callId, originPerfNow) {
  const key = String(callId || "");
  if (!key || traceStartByCallId.has(key) || typeof originPerfNow !== "number") {
    return;
  }
  traceStartByCallId.set(key, originPerfNow);
}

export function logCallStartupEvent(callId, event, role) {
  const key = String(callId || "");
  if (!key) {
    return;
  }
  if (!traceStartByCallId.has(key)) {
    startCallStartupTrace(key);
  }
  const startedAt = traceStartByCallId.get(key) || now();
  const elapsedMs = Math.max(0, Math.round(now() - startedAt));
  console.log(
    `[callStartup] event=${event} callId=${key} elapsedMs=${elapsedMs} role=${role || "unknown"}`
  );
}

export function clearCallStartupTrace(callId) {
  const key = String(callId || "");
  if (key) {
    traceStartByCallId.delete(key);
  }
}
